import { Book, LogOut, Settings, User } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useUser } from "@/hooks/useUser";
import ChangePasswordDialog from "@/components/ui/change-password-dialog";
import { useShopStore } from "@/store/shop";
import { ROUTES, SESSION_NAME } from '@/config/routes';

export default function UserMenu() {
  const { user, logout } = useUser();
  const { shopList, selectedShop, setSelectedShop } = useShopStore();
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
  const navigate = useNavigate();
  
  const handleChangePasswordDialogOpen = () => {
    setIsChangePasswordDialogOpen(true);
  };
  
  const handleChangePasswordDialogClose = (open: boolean) => {
    setIsChangePasswordDialogOpen(open);
  };
  
  const handleLogout = () => {
    logout();
  };

  const handleGuideOpen = () => {
    window.open('https://coggiri.gitbook.io/cworks/', '_blank', 'noopener,noreferrer');
  };

  const handleManagerOpen = () => {
    const authStorage = window.sessionStorage.getItem(SESSION_NAME);
    let authToken = null;
    if (authStorage) {
      const authData = JSON.parse(authStorage);
      authToken = authData?.state?.token;
    }
    window.open(`https://app.coggiri.ai.kr/cworks/manager?token=${authToken}`, '_blank', 'noopener,noreferrer');
  }

  const handleUserManagementOpen = () => {
    navigate(ROUTES.DASHBOARD.USER.MANAGEMENT);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className="cursor-pointer">
        <Avatar>
          <AvatarImage src="/assets/avatars/cworks.png" alt="User" />
          <AvatarFallback className="rounded-lg">
            {selectedShop?.companyName.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end">
        <DropdownMenuLabel className="p-0">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar>
              <AvatarImage src="/assets/avatars/cworks.png" alt="User" />
              <AvatarFallback className="rounded-lg">
                {user.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{selectedShop?.companyName}</span>
              <span className="text-muted-foreground truncate text-xs">{user.email}</span>
              {user.platformMallId && (
                <span className="text-muted-foreground truncate text-xs" style={{color: "gray"}}>({user.platformMallId})</span>
              )}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={handleChangePasswordDialogOpen} className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            비밀번호 재설정
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleGuideOpen} className="cursor-pointer">
            <Book className="mr-2 h-4 w-4" />
            이용가이드
          </DropdownMenuItem>
          {user.isManager && (
            <DropdownMenuItem onClick={handleManagerOpen}  className="cursor-pointer">
              <User className="mr-2 h-4 w-4" />
              관리자 페이지
            </DropdownMenuItem> 
          )}
          {user.isSuperAdmin && (
            <DropdownMenuItem onClick={handleUserManagementOpen}  className="cursor-pointer">
              <User className="mr-2 h-4 w-4" />
              사용 계정 관리
            </DropdownMenuItem> 
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer" onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          로그아웃
        </DropdownMenuItem>
      </DropdownMenuContent>
      
      <ChangePasswordDialog 
        open={isChangePasswordDialogOpen} 
        onOpenChange={handleChangePasswordDialogClose} 
      />
    </DropdownMenu>
  );
}
